import { useState } from "react";
import { api, type Meal, type MealItem } from "../lib/api";

type Props = {
  meal: Meal;
  onClose: () => void;
  onSaved: () => void;
};

export function EditMealDrawer({ meal, onClose, onSaved }: Props) {
  const [caption, setCaption] = useState(meal.caption ?? "");
  const [items, setItems] = useState<MealItem[]>(meal.items);
  const totalCalories = items.reduce((sum, it) => sum + it.calories, 0);
  const totalProtein  = items.reduce((sum, it) => sum + it.protein_g, 0);
  const [busy, setBusy] = useState(false);

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <aside className="drawer" onClick={(e) => e.stopPropagation()}>
        <header>
          <h3>Edit meal</h3>
          <button className="ghost" onClick={onClose}>
            Close
          </button>
        </header>

        <div className="totals-row">
          <span><strong>{Math.round(totalCalories)}</strong> kcal</span>
          <span><strong>{Math.round(totalProtein * 10) / 10}</strong> g protein</span>
        </div>
        <label>
          <span>Caption</span>
          <input value={caption} onChange={(e) => setCaption(e.target.value)} />
        </label>

        <h4>Items</h4>
        <div className="item-rows">
          {items.map((it, i) => (
            <div className="item-row" key={i}>
              <input
                value={it.name}
                onChange={(e) => updateItem(i, { name: e.target.value })}
                placeholder="name"
              />
              <input
                type="number"
                value={it.portion_g}
                onChange={(e) => updateItem(i, { portion_g: Number(e.target.value) })}
                placeholder="g"
              />
              <input
                type="number"
                value={it.calories}
                onChange={(e) => updateItem(i, { calories: Number(e.target.value) })}
                placeholder="kcal"
              />
              <input
                type="number"
                step="0.1"
                value={it.protein_g}
                onChange={(e) => updateItem(i, { protein_g: Number(e.target.value) })}
                placeholder="g protein"
              />
              <button className="ghost danger" onClick={() => removeItem(i)}>
                ×
              </button>
            </div>
          ))}
          <button
            className="ghost"
            onClick={() => setItems([...items, { name: "", portion_g: 0, calories: 0, protein_g: 0 }])}
          >
            + Add item
          </button>
        </div>

        <footer>
          <button
            className="primary"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api.patchMeal(meal.id, {
                  caption: caption || null,
                  items,
                });
                onSaved();
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </footer>
      </aside>
    </div>
  );

  function updateItem(i: number, patch: Partial<MealItem>) {
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  }
  function removeItem(i: number) {
    setItems((prev) => prev.filter((_, idx) => idx !== i));
  }
}
